// src/components/HomeAccessoriesShowcase.js
import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "./LanguageContext";
import { ShoppingBag, Gift, Wallet, Briefcase, Tags, Glasses } from "lucide-react";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME (შენი პალიტრის ტონი) ================= */
const UI = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  card: "#ffffff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};
const RAD = { md: "12px", lg: "16px", xl: "20px", pill: "999px" };

/* ============== ANIMS ============== */
const fadeInUp = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const pulseBorder = keyframes`0%{box-shadow:0 0 0 0 rgba(161,90,90,.18)}70%{box-shadow:0 0 0 8px rgba(161,90,90,0)}100%{box-shadow:0 0 0 0 rgba(161,90,90,0)}`;

/* ============== LAYOUT ============== */
const Wrap = styled.section`
  max-width: 1280px;
  margin: 24px auto 36px;
  padding: 0 clamp(10px,4vw,16px);
  display: grid;
  gap: 16px;
  animation: ${fadeInUp} .2s ease;
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1.2fr 1fr;

  @media (max-width: 1100px){ grid-template-columns: 1fr; }
`;

/* ============== LEFT — Copy card ============== */
const CopyCard = styled.div`
  background: ${UI.card};
  border: 1px solid ${UI.line};
  border-radius: ${RAD.xl};
  box-shadow: ${UI.shadow};
  padding: 18px;
`;

const Title = styled.h3`
  margin: 2px 0 10px 0;
  font-weight: 900;
  font-size: 1.15rem;
  color: ${UI.text};
`;
const Text = styled.p`
  margin: 0;
  color: ${UI.dim};
  line-height: 1.65;
  font-size: .98rem;
`;

const Bullets = styled.ul`
  margin: 12px 0 0;
  color: ${UI.text};
  font-weight: 700;
  li{ margin: 6px 0; }
`;

const CTArow = styled.div`
  display:flex; gap:8px; flex-wrap:wrap; margin-top: 14px;
`;

const PillBtn = styled.button`
  border: 1px solid ${UI.brand};
  background: ${UI.brand};
  color:#fff;
  border-radius: ${RAD.pill};
  height: 38px; padding: 0 14px;
  font-weight: 900; letter-spacing:.2px;
  cursor: pointer;
  transition: transform .15s ease, filter .15s ease, box-shadow .15s ease;
  &:hover{ transform: translateY(-1px); filter: brightness(.96); box-shadow: ${UI.shadow}; }
`;

const GhostBtn = styled(PillBtn)`
  background: #fff; color: ${UI.brand};
  border-color: ${UI.line};
`;

/* ============== RIGHT — Categories ============== */
const CategoriesCard = styled.div`
  background:#fff; border:1px solid ${UI.line}; border-radius:${RAD.xl}; box-shadow:${UI.shadow};
  padding: 16px;
`;

const CatHead = styled.div`
  display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:10px;
  strong{ color:${UI.text}; }
  span{ color:${UI.dim}; font-size:.92rem; }
`;

const CatsGrid = styled.div`
  display:grid; gap:10px;
  grid-template-columns: repeat(2, minmax(0,1fr));
  @media (max-width: 420px){ grid-template-columns: 1fr; }
`;

const CatCard = styled.button`
  display:flex; align-items:center; gap:10px;
  border:1px solid ${UI.line}; background:#fff; border-radius:${RAD.lg};
  padding:12px; text-align:left; cursor:pointer;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
  &:hover{ transform: translateY(-2px); box-shadow:${UI.shadow}; border-color: rgba(0,0,0,.12); }
  ${({$pulse}) => $pulse && css`animation:${pulseBorder} 2.2s ease infinite;`}
`;

const IconWrap = styled.div`
  width:36px; height:36px; border-radius:10px; border:1px solid ${UI.line}; display:grid; place-items:center; background:${UI.subtle};
`;
const CatText = styled.div`
  small{ display:block; color:${UI.dim}; line-height:1.2; }
  b{ display:block; color:${UI.text}; font-size:.98rem; }
`;

/* ============== HELPERS ============== */
const FALLBACK_CATEGORIES = [
  { key: "wallets",     ka: "ქაღალდის საფულეები", en: "Wallets",            icon: <Wallet size={16}/> },
  { key: "card-holders",ka: "ბარათების ჰოლდერები", en: "Card Holders",    icon: <Tags size={16}/> },
  { key: "bags",        ka: "ჩანთები",           en: "Bags",               icon: <ShoppingBag size={16}/> },
  { key: "briefcases",  ka: "ბრიფკესები",        en: "Briefcases",         icon: <Briefcase size={16}/> },
  { key: "gift-sets",   ka: "სასაჩუქრე სეტები",  en: "Gift Sets",          icon: <Gift size={16}/> },
  { key: "sunglasses",   ka: "მზის სათვალეები",  en: "Sunglasses",          icon: <Glasses  size={16}/> },
];

function pickCategoryField(p){
  // ვცდილობთ ვიპოვოთ ქვე-კატეგორიის სავარაუდო ველი
  return p.subcategory || p.type || p.accessory_type || p.accessories_category || "";
}

function uniqueSorted(arr){
  return Array.from(new Set(arr.filter(Boolean))).sort((a,b)=>a.localeCompare(b));
}

/* ============== COMPONENT ============== */
export default function HomeAccessoriesShowcase(){
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [rawCats, setRawCats] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try{
        const { data } = await api.get(`${API_BASE_URL}/get_products.php`);
        if(!mounted) return;
        const accessories = (data || []).filter(p => (p.category || "").toLowerCase() === "accessories");
        const derived = uniqueSorted(accessories.map(p => String(pickCategoryField(p)).trim()));
        setRawCats(derived);
      }catch(e){
        console.error("Accessories fetch error:", e);
        setRawCats([]);
      }finally{
        if(mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // აქ ვქმნით ვიზუალური ობიექტების სიას — თუ API-დან მოვიდნენ სახელები, შევუნახავთ label-ს და key-ს
  const categories = useMemo(() => {
    if(!rawCats.length) return FALLBACK_CATEGORIES;
    // map from raw text to simple objects; icon-ს დინამიურად ვერ დავადებთ, მაგრამ ok
    return rawCats.slice(0, 10).map((label, i) => ({
      key: label.toLowerCase().replace(/\s+/g, "-"),
      ka: label,  // თუ გაქვს თარგმანი, გამოიტანე ის; თორემ 그대로 ვაჩვენებთ
      en: label,
      icon: FALLBACK_CATEGORIES[i % FALLBACK_CATEGORIES.length].icon,
    }));
  }, [rawCats]);

  const goAllAccessories = () => {
    navigate(`/products?category=accessories`);
  };

  const goCategory = (label) => {
    // და კატეგორია „accessories“ წინასწარ მივუთითოთ:
  // გადაგვყავს accessories კატეგორიაზე და ვაშვებთ subcategory query-ს
  const qp = new URLSearchParams({
    category: "accessories",
    subcategory: label
  }).toString();
  navigate(`/products?${qp}`);
  };

  const T = {
    title_ka: "ჰენდმეიდ აქსესუარები — ხარისხიანი მასალებით",
    title_en: "Handcrafted Accessories — from premium materials",
    copy_ka:  "აღმოაჩინეთ ხელნაკეთი, ტყავის და პრემიუმ კლასის აქსესუარები. შექმნილია გამძლეობისა და ელეგანტურობისთვის - იდეალურია ყოველდღიური გამოყენებისთვის ან გააზრებული საჩუქრისთვის.",
    copy_en:  "Discover handmade, leather and premium-finished accessories. Built for durability and elegance — perfect for everyday use or as a thoughtful gift.",
    bullets_ka: [
      "ნატურალური ტყავი და მაღალი ხარისხის აქსესუარები",
      "ხელნაკეთი დეტალები და დახვეწილი დიზაინი",
      "კომფორტის პრიორიტეტული ფორმები ყოველდღიური ტარებისთვის",
    ],
    bullets_en: [
      "Genuine leather & high-grade hardware",
      "Handcrafted details and refined design",
      "Comfort-first shapes for everyday carry",
    ],
    cats_ka: "კატეგორიები",
    cats_en: "Categories",
    seeAll_ka: "ნახე ყველა აქსესუარი",
    seeAll_en: "See all accessories",
  };

  const isEn = language === "en";
  const TT = {
    title: isEn ? T.title_en : T.title_ka,
    copy: isEn ? T.copy_en : T.copy_ka,
    bullets: isEn ? T.bullets_en : T.bullets_ka,
    cats: isEn ? T.cats_en : T.cats_ka,
    all: isEn ? T.seeAll_en : T.seeAll_ka,
  };

  return (
    <Wrap>
      <Grid>
        {/* LEFT — Copy */}
        <CopyCard>
          <Title>{TT.title}</Title>
          <Text>{TT.copy}</Text>
          <Bullets>
            {TT.bullets.map((b,i)=><li key={i}>{b}</li>)}
          </Bullets>
          <CTArow>
            <PillBtn onClick={goAllAccessories}>{TT.all}</PillBtn>
          </CTArow>
        </CopyCard>

        {/* RIGHT — Category grid */}
        <CategoriesCard>
          <CatHead>
            <strong>{TT.cats}</strong>
            <span>{loading ? (isEn ? "loading…" : "იტვირთება…") : `${categories.length} ${isEn ? "types" : "ტიპი"}`}</span>
          </CatHead>

          <CatsGrid>
            {categories.map((c, idx) => (
              <CatCard key={c.key} onClick={()=>goCategory(isEn ? c.en : c.ka)} $pulse={idx===0 && !loading}>
                <IconWrap aria-hidden>{c.icon}</IconWrap>
                <CatText>
                  <b>{isEn ? c.en : c.ka}</b>
                  <small>{isEn ? "Explore" : "იხილე"}</small>
                </CatText>
              </CatCard>
            ))}
          </CatsGrid>
        </CategoriesCard>
      </Grid>
    </Wrap>
  );
}
